// FLAGS: identity, authorization
// Holds login state for the whole app and supplies the hasRole predicates the UI gates on.
import { googleLogout } from "@react-oauth/google";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import Loader from "@src/components/Loader";
import { OpenUpRoles } from "@src/services/OpenUpRoles";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { ResponseUserResponse } from "@src/api/v2/data-contracts";
import { Users as UsersV1 } from "@src/api/v1/Users";
import { setAuthCallbacks } from "@src/utils/axiosInterceptors";
import authenticationService, {
  clearUserFromStorage,
  getUserProfile,
} from "@src/services/authentication.service";

const { hasRole, isManagerOrAdmin, isManagerOrAdminOrHost } =
  authenticationService;

interface AuthenticationContextType {
  isLoggedIn: boolean;
  hasCheckedLogin: boolean;
  user: ResponseUserResponse | null;
  logout: () => void;
  setLoggedIn: () => void;
  hasRole: (...roles: OpenUpRoles[]) => boolean;
  isManagerOrAdmin: () => boolean;
  isManagerOrAdminOrHost: () => boolean;
}

const notImplemented = (name: string) => () => {
  throw new Error(`AuthenticationContext.${name} is not implemented`);
};

const AuthenticationContext = createContext<AuthenticationContextType>({
  isLoggedIn: false,
  hasCheckedLogin: false,
  user: null,
  logout: notImplemented("logout"),
  setLoggedIn: notImplemented("setLoggedIn"),
  hasRole: notImplemented("hasRole"),
  isManagerOrAdmin: notImplemented("isManagerOrAdmin"),
  isManagerOrAdminOrHost: notImplemented("isManagerOrAdminOrHost"),
});

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasCheckedLogin, setHasCheckedLogin] = useState(false);
  const [user, setUser] = useState<ResponseUserResponse | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // FLAGS: identity
  // Clears the user, local storage and the entire React Query cache; dropping removeQueries would leave
  // the previous operator's client data in memory for the next login on the same browser.
  const logout = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[Authorization] Logging out");
    googleLogout();
    new UsersV1(OpenUpHttpClient).logout();
    setUser(null);
    setIsLoggedIn(false);
    clearUserFromStorage();
    queryClient.removeQueries();
  }, [queryClient]);

  const setLoggedIn = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[Authorization] You are now logged in");
    setIsLoggedIn(true);
  }, []);

  // FLAGS: authorization
  // hasRole and the isManagerOrAdmin* predicates come straight from authentication.service (destructured
  // at the top of this file), not from the user state above, and they only decide what the UI shows -
  // every real access decision is the server's per-endpoint policy.
  const contextValues = useMemo(
    (): AuthenticationContextType => ({
      isLoggedIn,
      hasCheckedLogin,
      user,
      logout,
      setLoggedIn,
      hasRole,
      isManagerOrAdmin,
      isManagerOrAdminOrHost,
    }),
    [isLoggedIn, hasCheckedLogin, user, logout, setLoggedIn],
  );
  // FLAGS: identity
  // Decides isLoggedIn at boot from the refresh-token cookie. Every failure is logged and swallowed, so
  // an expired session and a broken profile endpoint both render as "logged out".
  const checkRefreshTokenValidity = useCallback(async () => {
    try {
      const userProfile = await getUserProfile();
      setUser(userProfile);
      setIsLoggedIn(true);
    } catch (e) {
      console.error(e);
    } finally {
      setHasCheckedLogin(true);
    }
  }, []);

  useEffect(() => {
    setAuthCallbacks({ logout, navigate, setIsLoggedIn, setUser });
  }, [logout, navigate]);

  useEffect(() => {
    checkRefreshTokenValidity();
  }, [checkRefreshTokenValidity]);

  return (
    <AuthenticationContext.Provider value={contextValues}>
      {hasCheckedLogin ? children : <Loader />}
    </AuthenticationContext.Provider>
  );
};

export const useAuthentication = () => useContext(AuthenticationContext);
